import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Wallet, Landmark, CreditCard, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const TYPES = [
  { key: "cash", label: "Nakit Kasa", icon: Wallet, tone: "text-amber-400", bg: "bg-amber-500/10" },
  { key: "bank", label: "Banka Hesapları", icon: Landmark, tone: "text-sky-400", bg: "bg-sky-500/10" },
  { key: "card", label: "Kredi Kartları", icon: CreditCard, tone: "text-violet-400", bg: "bg-violet-500/10" },
];
export const typeOf = (t) => TYPES.find((x) => x.key === t) || TYPES[0];
const empty = (type = "bank") => ({ name: "", type, opening: "", active: true, statement_day: "", due_day: "" });

export default function CashAccountsDialog({ open, onClose, onChanged }) {
  const [accounts, setAccounts] = useState([]);
  const [date, setDate] = useState("");
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, o] = await Promise.all([api.get("/cashbook/accounts"), api.get("/cashbook/opening")]);
    setAccounts(a.data); setDate(o.data.date || new Date().toISOString().slice(0, 10));
  };
  useEffect(() => { if (open) { load(); setEdit(null); } }, [open]);

  const saveDate = async (d) => {
    setDate(d);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
    try { await api.put("/cashbook/opening", { date: d }); onChanged(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    if (!edit.name.trim()) { toast.error("Hesap adı gerekli"); return; }
    setSaving(true);
    try {
      const body = { name: edit.name, type: edit.type, opening: parseFloat(edit.opening) || 0, active: edit.active,
        statement_day: edit.type === "card" && edit.statement_day ? parseInt(edit.statement_day, 10) : null, due_day: edit.type === "card" && edit.due_day ? parseInt(edit.due_day, 10) : null };
      if (edit.id) await api.put(`/cashbook/accounts/${edit.id}`, body); else await api.post("/cashbook/accounts", body);
      toast.success(edit.id ? "Hesap güncellendi" : "Hesap açıldı"); setEdit(null); load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`"${a.name}" ${a.entry_count ? "kapatılsın (pasife alınsın)" : "silinsin"} mi?`)) return;
    try { const r = await api.delete(`/cashbook/accounts/${a.id}`); toast.success(r.data.message); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (a) => {
    try { await api.put(`/cashbook/accounts/${a.id}`, { name: a.name, type: a.type, opening: a.opening, active: !a.active, statement_day: a.statement_day ?? null, due_day: a.due_day ?? null }); load(); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cash-accounts-dialog">
        <DialogHeader><DialogTitle className="font-heading">Hesaplar</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-muted/40 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Açılış tarihi</span>
          <Input type="date" value={date} onChange={(e) => saveDate(e.target.value)} data-testid="accounts-opening-date" className="h-8 w-40 font-mono" />
          <span className="text-xs text-muted-foreground">Açılış bakiyeleri bu tarihte geçerlidir.</span>
        </div>
        {TYPES.map((t) => {
          const list = accounts.filter((a) => a.type === t.key);
          return (
            <div key={t.key} data-testid={`accounts-group-${t.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className={`flex items-center gap-2 text-sm font-semibold ${t.tone}`}><t.icon className="h-4 w-4" /> {t.label}</div>
                <Button size="sm" variant="ghost" onClick={() => setEdit(empty(t.key))} data-testid={`accounts-add-${t.key}`} className="h-7 gap-1 text-xs"><Plus className="h-3.5 w-3.5" /> Hesap Aç</Button>
              </div>
              <div className="space-y-1.5">
                {list.length === 0 && <div className="text-xs text-muted-foreground px-3 py-2 rounded-lg border border-dashed border-border">Bu türde hesap yok</div>}
                {list.map((a) => (
                  <div key={a.id} data-testid={`account-row-${a.id}`} className={`flex items-center gap-3 rounded-lg border border-border/60 px-3 py-2 ${a.active ? "" : "opacity-50"}`}>
                    <div className={`h-8 w-8 rounded-md ${t.bg} ${t.tone} flex items-center justify-center shrink-0`}><t.icon className="h-4 w-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{a.name}{!a.active && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">Pasif</span>}</div>
                      <div className="text-[11px] text-muted-foreground">Açılış {TRY(a.opening)} · {a.entry_count} kayıt{a.type === "card" && a.due_day ? ` · kesim ${a.statement_day || "—"} / son ödeme ${a.due_day}` : ""}</div>
                    </div>
                    <button onClick={() => toggle(a)} title={a.active ? "Pasife al" : "Aktif et"} data-testid={`account-toggle-${a.id}`} className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground"><Power className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEdit({ ...a, opening: String(a.opening), statement_day: a.statement_day ? String(a.statement_day) : "", due_day: a.due_day ? String(a.due_day) : "" })} data-testid={`account-edit-${a.id}`} className="h-7 w-7 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(a)} data-testid={`account-delete-${a.id}`} className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {edit && (
          <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="account-form">
            <div className="text-sm font-semibold">{edit.id ? "Hesabı Düzenle" : "Yeni Hesap"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <F label="Hesap Adı"><Input autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && save()} data-testid="account-form-name" placeholder="Örn: Ziraat TL, Ana Kasa, Garanti Bonus" /></F>
              <F label="Tür">
                <Select value={edit.type} onValueChange={(v) => setEdit({ ...edit, type: v })}>
                  <SelectTrigger data-testid="account-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label={edit.type === "card" ? "Açılış Borcu (−) / Bakiye (₺)" : "Açılış Bakiyesi (₺)"}><Input type="number" step="0.01" value={edit.opening} onChange={(e) => setEdit({ ...edit, opening: e.target.value })} data-testid="account-form-opening" className="font-mono" placeholder="0,00" /></F>
            </div>
            {edit.type === "card" && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Hesap Kesim Günü (1-31)"><Input type="number" min={1} max={31} value={edit.statement_day} onChange={(e) => setEdit({ ...edit, statement_day: e.target.value })} data-testid="account-form-statement-day" className="font-mono" placeholder="Örn: 15" /></F>
                <F label="Son Ödeme Günü (1-31)"><Input type="number" min={1} max={31} value={edit.due_day} onChange={(e) => setEdit({ ...edit, due_day: e.target.value })} data-testid="account-form-due-day" className="font-mono" placeholder="Örn: 25" /></F>
              </div>
            )}
            {edit.type === "card" && <p className="text-xs text-muted-foreground">Kredi kartı: harcamalar bakiyeyi eksiye götürür (borç); karta yapılan ödeme (virman) borcu kapatır. Son ödeme günü girilirse Genel Bakış'ta 7 gün önceden hatırlatılır.</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Vazgeç</Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="account-form-save" className="gap-2">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet</Button>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="accounts-close">Kapat</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
